import { useEffect, useLayoutEffect, useState } from "react";
import SvgWrappableText from "./SvgWrappableText";


interface SvgCircleFitTextProps {
  text: string;
  cx: number;
  cy: number;
  r: number;
  color: string;
  fontSize: number;
}

export default function SvgCircleFitText(props: SvgCircleFitTextProps) {
  const { text, cx, cy, r, color, fontSize } = props;

  const [mainTextHeight, setMainTextHeight] = useState(0);
  const [adjustY, setAdjustY] = useState(0);
  const [bboxes, setBBoxes] = useState([]);
  const mainTextTop = cy + adjustY - 0.5 * (mainTextHeight);

  const handleMainTextLayout = (h, boxes) => {
    setMainTextHeight(h);
    setBBoxes(boxes);
  }

  const [recursion, setRecursion] = useState(0);
  const [maxTextReach, setMaxTextReach] = useState(0);
  const [adjustedFontSize, setAdjustedFontSize] = useState(fontSize);

  useLayoutEffect(() => {
    setRecursion(0);
    setAdjustedFontSize((prev) => {
      if (prev === fontSize) {
        return fontSize + 1;
      }
      return fontSize;
    });
  }, [text])

  useLayoutEffect(() => {
    if (!bboxes || bboxes.length === 0) {
      return;
    }
    const yOffset = mainTextTop - bboxes[0].y;
    const max = bboxes.reduce((acc, bbox) => {
      const getR = (x, y) => {
        const dx = x - cx;
        const dy = (y + yOffset) - cy;
        return Math.sqrt(dx * dx + dy * dy);
      }
      return Math.max(
        acc,
        [
          getR(bbox.x, bbox.y),
          getR(bbox.x, bbox.y + bbox.height),
          getR(bbox.x + bbox.width, bbox.y),
          getR(bbox.x + bbox.width, bbox.y + bbox.height),
        ].reduce((acc, v) => Math.max(acc, v), 0)
      );
    }, 0)
    setMaxTextReach(max);
    const w0 = bboxes[0].width;
    const w1 = bboxes[bboxes.length - 1].width;
    if (w1 > 0) {
      const ratio = w0 / w1;
      const maxAdjust = 0.1 * r;
      if (ratio > 2) {
        setAdjustY(maxAdjust);
      } else if (ratio < 0.5) {
        setAdjustY(-maxAdjust);
      } else if (ratio < 1) {
        const f = (1 / ratio) - 1;
        setAdjustY(-f * maxAdjust);
      } else {
        const f = ratio - 1;
        setAdjustY(f * maxAdjust);
      }
    }
  }, [bboxes]);

  useEffect(() => {
    if (maxTextReach < 1 || recursion >= 20) {
      return;
    }
    if (maxTextReach > r) {
      setTimeout(() => {
        setAdjustedFontSize(adjustedFontSize - 0.04 * fontSize);
        setRecursion(recursion + 1);  
      }, 5);
    }
  }, [maxTextReach])

  return (
      <SvgWrappableText
        text={text}
        color={color}
        x={cx}
        top={mainTextTop}
        fontFamily='Montserrat'
        fontSize={adjustedFontSize}
        fontWeight="600"
        width={r * 1.6}
        textAnchor="middle"
        onLayout={handleMainTextLayout}
      />
  )
}
